import api from "../../../api";

// api's baseURL already ends in /api — strip a leading /api from URLs returned by
// endpoints that build absolute paths (e.g. lead document `url` fields).
function normalizeApiPath(path) {
  return path && path.startsWith('/api/') ? path.slice(4) : path;
}

export async function downloadDocument(downloadUrl, fileName) {
  const res = await api.get(normalizeApiPath(downloadUrl), { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', fileName || 'document');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export async function getProposalsByCreator(userId) {
  try {
    const res = await api.get('/crm/proposals', { params: { createdBy: userId, size: 1000 } });
    return Array.isArray(res.data?.content) ? res.data.content : [];
  } catch (error) {
    console.error('Error fetching proposals by creator:', error);
    return [];
  }
}

export async function getProjectsByCreator(userId) {
  try {
    const res = await api.get('/crm/projects', { params: { createdById: userId, size: 1000 } });
    return Array.isArray(res.data?.content) ? res.data.content : [];
  } catch (error) {
    console.error('Error fetching projects by creator:', error);
    return [];
  }
}

export async function getLeadsByOwner(userId) {
  try {
    const res = await api.get('/crm/leads', { params: { viewOwnUserId: userId, size: 1000 } });
    return Array.isArray(res.data?.content) ? res.data.content : [];
  } catch (error) {
    console.error('Error fetching leads by owner:', error);
    return [];
  }
}

export async function getProposalDocuments(proposalId) {
  try {
    const res = await api.get(`/crm/proposal-documents/proposal/${proposalId}`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching documents for proposal ${proposalId}:`, error);
    return [];
  }
}

export async function getProjectDocuments(projectId) {
  try {
    const res = await api.get(`/crm/project-documents/project/${projectId}`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching documents for project ${projectId}:`, error);
    return [];
  }
}

export async function getLeadDocuments(leadId) {
  try {
    const res = await api.get(`/crm/leads/${leadId}/documents`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching documents for lead ${leadId}:`, error);
    return [];
  }
}

// Aggregates documents from Proposals, Projects, and Leads created by the given user.
export async function getUserDocuments(userId) {
  const [proposals, projects, leads] = await Promise.all([
    getProposalsByCreator(userId),
    getProjectsByCreator(userId),
    getLeadsByOwner(userId),
  ]);

  const ownProposals = (Array.isArray(proposals) ? proposals : []);
  const ownProjects = (Array.isArray(projects) ? projects : []);
  const ownLeads = (Array.isArray(leads) ? leads : [])
    .filter((lead) => String(lead.createdBy?.id ?? lead.createdById ?? '') === String(userId));

  const [proposalDocLists, projectDocLists, leadDocLists] = await Promise.all([
    Promise.all(ownProposals.map((p) => getProposalDocuments(p.id).then((docs) => ({ record: p, docs })))),
    Promise.all(ownProjects.map((p) => getProjectDocuments(p.id).then((docs) => ({ record: p, docs })))),
    Promise.all(ownLeads.map((l) => getLeadDocuments(l.id).then((docs) => ({ record: l, docs })))),
  ]);

  const result = [];

  proposalDocLists.forEach(({ record, docs }) => {
    (Array.isArray(docs) ? docs : []).forEach((doc) => {
      result.push({
        id: `proposal-${doc.id}`,
        category: doc.documentType || 'Proposal Document',
        documentName: doc.originalFileName || doc.fileName || '-',
        fileName: doc.fileName || '-',
        fileSize: doc.fileSize,
        uploadDate: doc.createdAt,
        uploadBy: doc.createdBy || '-',
        status: 'Active',
        source: 'Proposal',
        sourceName: record.companyName || `Proposal #${record.id}`,
        downloadUrl: `/crm/proposal-documents/download/${doc.id}`,
      });
    });
  });

  projectDocLists.forEach(({ record, docs }) => {
    (Array.isArray(docs) ? docs : []).forEach((doc) => {
      result.push({
        id: `project-${doc.id}`,
        category: doc.documentType || 'Project Document',
        documentName: doc.originalFileName || doc.fileName || '-',
        fileName: doc.fileName || '-',
        fileSize: doc.fileSize,
        uploadDate: doc.createdAt,
        uploadBy: doc.uploadedBy || '-',
        status: 'Active',
        source: 'Project',
        sourceName: record.projectCode || record.companyName || `Project #${record.id}`,
        downloadUrl: `/crm/project-documents/download/${doc.id}`,
      });
    });
  });

  leadDocLists.forEach(({ record, docs }) => {
    (Array.isArray(docs) ? docs : []).forEach((doc) => {
      result.push({
        id: `lead-${doc.id}`,
        category: doc.documentType || 'Lead Document',
        documentName: doc.originalFileName || doc.fileName || '-',
        fileName: doc.fileName || '-',
        fileSize: doc.fileSize,
        uploadDate: doc.createdAt,
        uploadBy: doc.uploadedBy || '-',
        status: 'Active',
        source: 'Lead',
        sourceName: record.projectName || record.title || `Lead #${record.id}`,
        downloadUrl: doc.url,
      });
    });
  });

  return result.sort((a, b) => new Date(b.uploadDate || 0) - new Date(a.uploadDate || 0));
}
